import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { useBranding } from 'modules/Common/hooks/useBranding';
import { resolveVideo } from './OnboardingVideoLightbox';

/**
 * The walkthrough, as a thumbnail rather than a player.
 *
 * A 16:9 frame beside the step's how-to lines: too small to watch in, which is
 * the point — clicking dims the page and plays the walkthrough over the middle
 * of it (`OnboardingVideoLightbox`). Not a modal: the step's how-to is already
 * open in the card underneath, so a dialog that redraws the title, the same
 * markdown and a footer around the player was frame around nothing.
 *
 * Renders nothing without a video, and nothing on a whitelabel build: the
 * walkthroughs are CG-branded content, the same gate the modal applies.
 *
 * The still is best-effort and the frame is designed to look finished without
 * one — a tinted box with the play badge and the caption. Only YouTube hands
 * out a thumbnail for free; a file we serve ourselves yields its own first
 * frame; every other host (Vimeo, Loom, whatever we land on) would need an
 * oEmbed round-trip per step, which is not worth it for a 128px tile.
 */

const OnboardingStepVideo = ({ videoUrl, title, onPlay }) => {
  const { t } = useTranslation('onboarding');
  const brand = useBranding();
  const [stillFailed, setStillFailed] = useState(false);

  if (!videoUrl || brand.key !== 'carbonglance') return null;

  const video = resolveVideo(videoUrl);
  const still = stillFailed ? null : video;
  // `title` is the step's server-supplied name, so it interpolates rather than
  // concatenating — the phrase's word order is the translator's to decide.
  const watchLabel = t('video.watchFor', 'Watch the walkthrough for {{step}}', { step: title });

  return (
    <button
      type="button"
      onClick={onPlay}
      title={watchLabel}
      aria-label={watchLabel}
      className="group relative shrink-0 w-[128px] h-[72px] rounded-lg overflow-hidden border border-slate-200 bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
    >
      {/* `mqdefault` is 320×180 — the smallest 16:9 still YouTube serves, so
          the frame never pays for a full-size image it renders at 128px. */}
      {still?.poster && (
        <img
          src={still.poster}
          alt=""
          loading="lazy"
          onError={() => setStillFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* A file we host: its own first frame, at metadata cost rather than a
          download. `#t=0.5` is what makes browsers seek off a black frame 0. */}
      {still?.kind === 'file' && (
        <video
          src={`${still.src}#t=0.5`}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden="true"
          onError={() => setStillFailed(true)}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      )}
      <span className="absolute inset-0 bg-slate-900/25 group-hover:bg-slate-900/35 transition-colors" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-7 h-7 rounded-full bg-white/90 inline-flex items-center justify-center shadow-sm">
          <FontAwesomeIcon
            icon={faPlay}
            className="text-primaryColor text-[10px] ml-px"
          />
        </span>
      </span>
      <span className="absolute bottom-1 left-1 right-1 text-[9px] font-semibold uppercase tracking-wider text-white/90 text-center">
        {t('video.walkthrough', 'walkthrough')}
      </span>
    </button>
  );
};

export default OnboardingStepVideo;
